import { noop } from '@utils/noop';
import { Dispatch, createContext, useContext } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { ObjectSchema, date, object, string } from 'yup';

export type CreateCampaignFormState = {
  providerId: string;
  startDate: Date | string | null;
  endDate: Date | string | null;
  description: string;
};

const defaultStartDate = new Date();
const defaultEndDate = new Date(defaultStartDate);
defaultEndDate.setDate(defaultEndDate.getDate() + 1);

export const createCampaignFormStateDefault: CreateCampaignFormState = {
  providerId: '',
  startDate: defaultStartDate.toISOString(),
  endDate: defaultEndDate.toISOString(),
  description: '',
};

const createCampaignEndDateField = () =>
  date()
    .required('end date is required field')
    .test('end-after-start', 'end date must be later than start date', function (value) {
      const { startDate } = this.parent as Partial<CreateCampaignFormState>;
      if (!value) return true;
      if (!startDate) return true;
      return new Date(value).getTime() > new Date(startDate).getTime();
    });

export const createCampaignSchema = (): ObjectSchema<Partial<CreateCampaignFormState>> => {
  return object<Partial<CreateCampaignFormState>>().shape({
    providerId: string().required('provider id is required field'),
    startDate: date()
      .required('start date is required field')
      // .typeError('start date must be a valid date')
      .test('start-date-not-in-past', 'start date must be today or later', (value) => {
        if (!value) {
          return true;
        }

        const dateValue = new Date(value);
        const startOfToday = new Date();
        startOfToday.setHours(0, 0, 0, 0);

        return dateValue.getTime() >= startOfToday.getTime();
      }),
    endDate: createCampaignEndDateField(),
    description: string().required('description is required field'),
  });
};
export const createCampaignSchemaDefault: ObjectSchema<Partial<CreateCampaignFormState>> = object<
  Partial<CreateCampaignFormState>
>().shape({
  providerId: string().required('provider id is required field'),
  startDate: date()
    .required('start date is required field')
    // .typeError('start date must be a valid date')
    .test('start-date-not-in-past', 'start date must be today or later', (value) => {
      if (!value) {
        return true;
      }

      const dateValue = new Date(value);
      const startOfToday = new Date();
      startOfToday.setHours(0, 0, 0, 0);

      return dateValue.getTime() >= startOfToday.getTime();
    }),
  endDate: createCampaignEndDateField(),
  description: string().required('description is required field'),
});

type CreateCampaignFormContextType = {
  schema: ObjectSchema<Partial<CreateCampaignFormState>>;
  setSchema: Dispatch<ObjectSchema<Partial<CreateCampaignFormState>>>;
  methods?: UseFormReturn<CreateCampaignFormState>;
};

const createCampaignFormContextDefault = {
  schema: createCampaignSchemaDefault,
  setSchema: noop,
} satisfies CreateCampaignFormContextType;

export const CreateCampaignFormContext = createContext<CreateCampaignFormContextType>(
  createCampaignFormContextDefault
);

export const useCreateCampaignForm = () => {
  const { methods } = useContext(CreateCampaignFormContext);
  if (!methods) {
    throw new Error('CreateCampaignFormContext is empty');
  }

  return methods;
};

export const useManageCreateCampaignSchema = () => {
  const { setSchema } = useContext(CreateCampaignFormContext);

  return { setSchema };
};
